// organize-imports-ignore
import React from "react";
import ReactDOM from "react-dom";
import { Router, Switch, Route as UnwrappedRoute } from "react-router-dom";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom-v5-compat";

import type { AppUrlOpen, AppState as CapAppState } from "@capacitor/app";
import { App as CapApp } from "@capacitor/app";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import * as Sentry from "@sentry/react";
import type { History } from "history";

import { ViewportContextProvider } from "~/common/ViewportContext";
import FullPageError from "~/error/FullPageError";
import { CompanionModeTitleBar } from "~/layout/TitleBar/CompanionModeTitleBar";
import TitleBarProvider from "~/layout/TitleBar/TitleBarProvider";
import growthbook from "~/lib/ab/growthbook";
import { AuthProvider } from "~/lib/auth/index";
import { initBrowserSupports } from "~/lib/browserSupport";
import { ensureAppUpdated } from "~/lib/ensureAppUpdated";
import history from "~/lib/history";
import { configureIntune } from "~/lib/intune/intune";
import { isDeployError, reloadForStaleChunks, retryDynamicImport } from "~/lib/retryDynamicImport";
import { inCompanionMode, isDesktopApp, isNative } from "~/lib/utils/platform";
import { desktopVersion } from "~/lib/version";
import Lowda, { StaticLowda } from "~/ui/loading/Lowda";
import { StylesheetsProvider } from "~/uxSimplificationFeature/StylesheetsProvider";

// TODO: Once we're done moving over to the new UX, import these here
// import "~/css/legacy-semantic.css";
// import "~/css/text-variables.css";
// import "~/css/base.css";
import "~/css/App.css";
import "~/css/tailwind.css";
import "~/ui/variables.css";

window.HISTORY = history;

// https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
const Route = Sentry.withSentryRouting(UnwrappedRoute);

// Sentry configuration
if (window && window.SENTRY_DSN_PUBLIC) {
	Sentry.init({
		dsn: window.SENTRY_DSN_PUBLIC,
		environment: window.SENTRY_ENV,
		release: window.APP_VERSION,
		// If we load the electron sentry in this context before browser sentry loads
		// auto session tracking breaks the desktop app.
		autoSessionTracking: !isDesktopApp,
		maxBreadcrumbs: 100,
		beforeBreadcrumb(crumb) {
			if (
				crumb.category === "xhr" &&
				["https://api.segment.io", "https://stats.g.doubleclick.net", "https://in.hotjar.com"].some(e =>
					crumb.data?.url?.startsWith(e),
				)
			) {
				return null;
			}
			return crumb;
		},
		beforeSend(event, hint) {
			// This error is fired by the Outlook Safe Links scraper by some of their
			// internal operations. The scraper also fires a ton more errors that we
			// can't do anything about, so if we detect it, we deactivate Sentry for
			// this session.
			// For more info about these errors see: https://github.com/getsentry/sentry-javascript/issues/3440#issuecomment-865857552
			// and https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/20
			if (
				typeof hint?.originalException === "string" &&
				hint.mechanism?.type === "onunhandledrejection" &&
				hint.originalException.startsWith("Object Not Found Matching Id:")
			) {
				Sentry.close(0);
				return null;
			}
			return event;
		},
		// Filter out third-party "Timeout" rejections (likely from reCAPTCHA)
		// These are non-Error promise rejections that we can't control or debug
		// See: https://github.com/getsentry/sentry-javascript/issues/2514
		ignoreErrors: ["Non-Error promise rejection captured with value: Timeout"],
		integrations: [
			Sentry.thirdPartyErrorFilterIntegration({
				filterKeys: ["fellow-app-unique-key-9DLSlDeFzMKrig"],
				// This will filter out stack traces that do not contain any of our functions
				behaviour: "drop-error-if-exclusively-contains-third-party-frames",
			}),
			Sentry.reactRouterV6BrowserTracingIntegration({
				useEffect: React.useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
		],
		tracesSampleRate: process.env.NODE_ENV === "production" ? 0.05 : 1.0,
	});

	const currentScope = Sentry.getCurrentScope();
	currentScope.setTag("client_session_id", window.CLIENT_SESSION_ID);
	if (window.INITIAL_STATE?.account?.id) {
		currentScope.setTag("account_id", String(window.INITIAL_STATE.account.id));
	}
	if (window.INITIAL_STATE?.user?.id) {
		currentScope.setUser({ id: String(window.INITIAL_STATE.user.id) });
	}
	try {
		const resolvedDesktopVersion = desktopVersion();
		if (resolvedDesktopVersion) {
			currentScope.setTag("desktop_app_version", resolvedDesktopVersion.join("."));
		}
	} catch (error) {
		Sentry.captureException(error);
	}
}

interface ErrorBoundaryState {
	error: boolean;
}

class ErrorBoundary extends React.Component<{}, ErrorBoundaryState> {
	state: ErrorBoundaryState = { error: false };

	componentDidCatch(error: Error) {
		console.error("Router caught error:", error);

		// When a dynamic import fails and the browser caches the failure,
		// retries return the same cached error. A full page reload clears
		// the module cache and gives the browser a fresh chance. The
		// sessionStorage guard prevents infinite reloads if the user is
		// offline or a proxy is persistently blocking cdn.fellow.app.
		if (isDeployError(error) && reloadForStaleChunks()) {
			return;
		}

		if (window && window.SENTRY_DSN_PUBLIC) {
			Sentry.captureException(error);
		}

		return this.setState({ error: true });
	}

	render() {
		if (this.state.error) {
			return <FullPageError message="Oops — Fellow failed to load" />;
		}

		return this.props.children;
	}
}

async function appListeners(history: History) {
	CapApp.addListener("appStateChange", (state: CapAppState) => {
		console.debug("App state changed. Is active?", state.isActive);
	});

	CapApp.addListener("appUrlOpen", (data: AppUrlOpen) => {
		if (data.url.startsWith("msal")) {
			// these links are microsoft authentication links, they're handled separately by the capacitor-oauth2 plugin.
			return;
		}
		const url = new URL(data.url);

		let pathname = url.pathname;

		// Login tickets require a full browser navigation to work, a history push isn't sufficient for the authentication
		if (window.location.host !== url.host || url.pathname.startsWith("/auth/login/ticket")) {
			window.location.href = url.href;
			return;
		}

		if (url.pathname.startsWith("/auth/login")) {
			const next = url.searchParams.get("next");
			if (next != null) {
				pathname = decodeURIComponent(next);
			}
		}

		history.push(pathname);
	});
}

function onServiceWorkerMessage(event: MessageEvent) {
	if (typeof event.data !== "object" || event.data === null) {
		return;
	}

	const { action } = event.data;

	if (action === "delegationStatusChange") {
		setTimeout(() => {
			window.location.reload();
		}, 1000);
	}
}

function serviceWorkerListeners() {
	const sw = window.navigator.serviceWorker;
	if (sw) {
		sw.addEventListener("message", onServiceWorkerMessage);
	}
}

const Email = React.lazy(() => retryDynamicImport(() => configureIntune(ensureAppUpdated(import("~/email")))));
const Auth = React.lazy(() => retryDynamicImport(() => ensureAppUpdated(import("~/auth"))));
const Onboarding = React.lazy(() =>
	retryDynamicImport(() => configureIntune(ensureAppUpdated(import("~/onboarding")))),
);
const Public = React.lazy(() => retryDynamicImport(() => configureIntune(ensureAppUpdated(import("~/public")))));
const Apps = React.lazy(() => retryDynamicImport(() => configureIntune(ensureAppUpdated(import("~/integrations")))));
const App = React.lazy(() => retryDynamicImport(() => configureIntune(ensureAppUpdated(import("~/App")))));
const Guest = React.lazy(() => retryDynamicImport(() => configureIntune(ensureAppUpdated(import("~/guest")))));
const CompanionMode = React.lazy(() =>
	retryDynamicImport(() => configureIntune(ensureAppUpdated(import("~/companionMode/CompanionMode")))),
);

if ("serviceWorker" in navigator && navigator.serviceWorker.register) {
	window.addEventListener("load", function () {
		if (!window.Cypress) {
			navigator.serviceWorker.register("/serviceWorkerRoot.js", {});
		}
	});
}

const Root = () => {
	if (isNative) appListeners(history);
	serviceWorkerListeners();

	return (
		<AuthProvider>
			<ViewportContextProvider>
				<GrowthBookProvider growthbook={growthbook}>
					<StylesheetsProvider>
						<Router history={history}>
							<TitleBarProvider>
								{/* Adding this here from the CompanionMode component */}
								{/* We need this to be available regardless of the new UX flag */}
								{/* Once the new UX is fully deployed, we can remove this */}
								{/* And move it back to the CompanionMode component */}
								{inCompanionMode && <CompanionModeTitleBar />}
								<ErrorBoundary>
									<React.Suspense fallback={<StaticLowda />}>
										<Switch>
											<Route path="/emails/" component={Email} />
											<Route path="/auth/" component={Auth} />
											<Route path="/o/" component={Onboarding} />
											<Route path="/public/" component={Public} />
											<Route path="/apps/" component={Apps} />
											<Route path="/guest/" component={Guest} />
											<Route path="/companion-mode/" component={CompanionMode} />
											<Route path="/" component={App} />
										</Switch>
									</React.Suspense>
									<Lowda />
								</ErrorBoundary>
							</TitleBarProvider>
						</Router>
					</StylesheetsProvider>
				</GrowthBookProvider>
			</ViewportContextProvider>
		</AuthProvider>
	);
};
function main(): void {
	ReactDOM.render(<Root />, document.getElementById("root"));
}

main();

if (window) {
	window.addEventListener("keydown", (e: KeyboardEvent) => {
		if ([8, 32].includes(e.keyCode) && window.document && e.target == window.document.body) {
			e.preventDefault();
		}
	});

	initBrowserSupports();
	document.documentElement.classList.toggle("Nav--ReducedMainWidth");
}
